import { ThreeDKeys } from 'src/app/core/constants/3D/3d-keys';
import { Simulation } from 'src/app/core/models/simulation.model';
import {
  BufferGeometry,
  CircleGeometry,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  Path,
  Vector3,
} from 'three';
import { Text } from 'troika-three-text';
import { Catenary3D } from '../../catenary-answer';
import { Point3d } from '../../point3d.model';
import { ReferenceSystemType } from '../../reference-system/reference-system-type.enum';
import { ReferenceSystem } from '../../reference-system/reference-system.model';
import ObjectBuilder from './object-builder.model';

export class ReferenceSystemBuilder extends ObjectBuilder {
  constructor() {
    super();
  }

  private get referenceSystem(): Group {
    return this._object;
  }

  private set referenceSystem(referenceSystem) {
    this._object = referenceSystem;
  }

  async build(
    riser: Catenary3D,
    _simulation: Simulation,
    referenceSystem: ReferenceSystem,
  ): Promise<Group> {
    this.referenceSystem = new Group();
    this.referenceSystem.name = `[${referenceSystem.title}].${referenceSystem.internalId}`;
    this.referenceSystem.position.set(
      referenceSystem.coordinates.x,
      referenceSystem.coordinates.y,
      referenceSystem.coordinates.z,
    );
    this.addMarker(riser, referenceSystem);
    this.addAxes(referenceSystem.rotation);
    return this.referenceSystem;
  }

  createGeometry(
    _catenary3D: Catenary3D,
    referenceSystem: ReferenceSystem,
  ): CircleGeometry | BufferGeometry {
    const isAttachedToConnection = this.isAttachedToConnection(referenceSystem);

    if (isAttachedToConnection) {
      return new BufferGeometry().setFromPoints(
        new Path().absarc(0, 0, 3, 0, Math.PI * 2, true).getSpacedPoints(30),
      );
    }

    return new CircleGeometry(2, 12);
  }

  createMaterial(
    _catenary3D: Catenary3D,
    referenceSystem: ReferenceSystem,
  ): MeshBasicMaterial | LineBasicMaterial {
    const isAttachedToConnection = this.isAttachedToConnection(referenceSystem);

    if (isAttachedToConnection) {
      return new LineBasicMaterial({
        color: referenceSystem.graphicProperties.color,
        linewidth: 3,
      });
    }

    return new MeshBasicMaterial({
      color: referenceSystem.graphicProperties.color,
    });
  }

  addMarker(riser: Catenary3D, referenceSystem: ReferenceSystem) {
    const markerGroup = new Group();
    const label: Text = new Text();
    label.text = referenceSystem.title;
    label.name = ThreeDKeys.THREEJS_OBJECTS_LABEL_NAME;
    label.position.x = 5;

    const isAttachedToConnection = this.isAttachedToConnection(referenceSystem);
    label.position.y = isAttachedToConnection ? -10 : 15;

    label.fontSize = 10;
    label.color = referenceSystem.graphicProperties.color;
    markerGroup.add(label);

    const material = this.createMaterial(riser, referenceSystem);
    const geometry = this.createGeometry(riser, referenceSystem);

    if (isAttachedToConnection) {
      const circle = new Line(geometry, material);
      markerGroup.add(circle);
    } else {
      const point = new Mesh(geometry, material);
      markerGroup.add(point);
    }

    this.referenceSystem.add(markerGroup);
  }

  addAxes(rotation: Point3d) {
    const axesGroup = new Group();

    const pointsX = [new Vector3(0, 0, 0), new Vector3(40, 0, 0)];
    const geometryX = new BufferGeometry().setFromPoints(pointsX);
    const materialX = new LineBasicMaterial({ color: 0xff1733 });
    materialX.linewidth = 2;
    const axisX = new Line(geometryX, materialX);
    axesGroup.add(axisX);

    const pointsY = [new Vector3(0, 0, 0), new Vector3(0, 40, 0)];
    const geometryY = new BufferGeometry().setFromPoints(pointsY);
    const materialY = new LineBasicMaterial({ color: 0x00ff2b });
    materialY.linewidth = 2;
    const axisY = new Line(geometryY, materialY);
    axesGroup.add(axisY);

    const pointsZ = [new Vector3(0, 0, 0), new Vector3(0, 0, 40)];
    const geometryZ = new BufferGeometry().setFromPoints(pointsZ);
    const materialZ = new LineBasicMaterial({ color: 0x2915ff });
    materialZ.linewidth = 2;
    const axisZ = new Line(geometryZ, materialZ);
    axesGroup.add(axisZ);

    axesGroup.rotation.set(rotation.x, rotation.y, rotation.z);
    this.referenceSystem.add(axesGroup);
  }

  transform(referenceSystem: ReferenceSystem): void {}

  focus(): void {}

  isAttachedToConnection(referenceSystem: ReferenceSystem): boolean {
    return (
      referenceSystem.type === ReferenceSystemType.ATTACHED_TO_NODE &&
      referenceSystem.structureOfAttachment === -1
    );
  }
}
